import { createFileRoute, notFound } from '@tanstack/react-router';
import { CircleCheckBig, Pencil } from 'lucide-react';
import { useState } from 'react';
import { DefaultNotFound } from '~/components/DefaultNotFound';
import TableWithThumbnails from '~/components/TableWithThumbnails';
import { PacksTierTags } from '~/partials/Tags';
import { ProgressBar } from '~/primitives/progressbar';
import { ScrollArea } from '~/primitives/scroll-area';
import { seo } from '~/util';
import { PackLevel } from '~/util/types/packs';
import { createOGPackPayload } from './-og';
import { convertOGDataToPayload } from '~/util/workers';
import { useScopes } from '~/components/Providers/AuthProvider';
import { EditPackSheet } from './-EditPackSheet';
import { packCompletionsQuery } from '~/util/queries/public/other';
import { CompletedByUsers } from '~/components/CompletedByUsers';

export const Route = createFileRoute('/packs/$packid')({
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { packtiers, queryClient },
        params: { packid },
        deps: { list },
    }) => {
        const tier = packtiers?.find((tier) =>
            tier.packs.find((pack) => pack.id === packid),
        );
        const pack = tier?.packs.find((pack) => pack.id === packid);

        if (!tier) {
            throw notFound({ data: 'Tier not found' });
        }

        if (!pack) {
            throw notFound({ data: 'Pack not found' });
        }

        return {
            pack,
            tier,
            completions: await queryClient.fetchQuery(
                packCompletionsQuery({ list, packId: packid }),
            ),
            encodedOgPackData: await convertOGDataToPayload(
                createOGPackPayload({ pack, tier }),
            ),
        };
    },
    head: async ({ loaderData }) => {
        return {
            meta: seo({
                title: loaderData?.pack?.name || 'Unknown Pack',
                image: loaderData?.encodedOgPackData
                    ? import.meta.env.VITE_OG_BASE_URL +
                      `/packs?data=${loaderData?.encodedOgPackData}`
                    : undefined,
                imageAlt: `Thumbnail for ${loaderData?.pack?.name || 'Unknown Pack'}`,
                fullImage: true,
            }),
        };
    },
    pendingMs: 0,
    wrapInSuspense: true,
    component: Pack,
    notFoundComponent: () =>
        DefaultNotFound({ customNotFound: 'Pack not found.' }),
});

function Pack() {
    const { pack, tier, completions } = Route.useLoaderData();
    const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
    const { hasScope } = useScopes();

    const [isEditOpen, setIsEditOpen] = useState(false);

    const nbCompleted = pack.levels.reduce((acc: number, level: PackLevel) => {
        if (level.completed_by_user) {
            return acc + 1;
        }
        return acc;
    }, 0);

    return (
        <div className="flex flex-col gap-3 w-full">
            <ScrollArea
                viewportRef={setViewport}
                className="flex flex-col gap-3 items-start h-[calc(100vh-170px)] w-full"
            >
                <div className="flex justify-between">
                    <h2 className="flex items-center gap-2 text-4xl">
                        {pack?.name}
                        {pack.levels.length === nbCompleted && (
                            <CircleCheckBig
                                className="text-primary"
                                size={48}
                            />
                        )}
                    </h2>
                    {hasScope('pack_modify') && (
                        <Pencil
                            className="h-8 w-8 hover:text-secondary-light transition-colors cursor-pointer"
                            onClick={() => {
                                setIsEditOpen(true);
                            }}
                        />
                    )}
                </div>

                <div className="flex flex-col my-2 gap-4 lg:flex-row lg:justify-between w-full">
                    <div className="flex gap-2 items-center">
                        {<PacksTierTags item={tier} />}
                        <p>+{pack.points / 10} points</p>
                    </div>
                    <div className="flex items-center">
                        <p className="text-xl">
                            {nbCompleted}/{pack.levels.length} levels completed
                        </p>
                    </div>
                </div>
                <div className="w-full">
                    <ProgressBar
                        value={nbCompleted}
                        max={pack.levels.length}
                        isLabelVisible={false}
                        animateOnRender
                        className="bg-secondary-light"
                        indicatorClassName="bg-primary"
                        height="10px"
                        width="100%"
                    />
                </div>
                <h3 className="text-3xl/16 font-semibold">Levels</h3>
                <TableWithThumbnails
                    data={pack.levels}
                    showCompletedByUser
                    showPoints
                    scrollElement={viewport}
                />
                <h3 className="text-3xl/16 font-semibold mt-4">
                    {completions.length > 0
                        ? `Victors (${completions.length})`
                        : 'No Victors'}
                </h3>
                <CompletedByUsers
                    completions={completions}
                    scrollElement={viewport}
                />
            </ScrollArea>
            {hasScope('pack_modify') && (
                <EditPackSheet
                    packtier={tier}
                    pack={pack}
                    open={isEditOpen}
                    onOpenChange={setIsEditOpen}
                />
            )}
        </div>
    );
}
